import React, { createContext, useState, useEffect, useContext } from 'react';
import { STORAGE_KEYS } from '../utils/constants';
import { toast } from 'react-toastify';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    // Load user from localStorage on mount
    const storedUser = localStorage.getItem(STORAGE_KEYS.USER_INFO);
    if (storedUser) {
      try {
        setUserInfo(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing user info:', error);
        localStorage.removeItem(STORAGE_KEYS.USER_INFO);
      }
    }
    setLoading(false);
  }, []);

  // Show toast messages
  useEffect(() => {
    if (message) {
      toast.info(message);
      // Clear message after showing
      const timer = setTimeout(() => setMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const login = (userData) => {
    setUserInfo(userData);
    localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(userData));
    if (userData.token) {
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, userData.token);
    }
  };

  const logout = async () => {
    setUserInfo(null);
    localStorage.removeItem(STORAGE_KEYS.USER_INFO);
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    setMessage('Logout successful!');
  };

  const updateUser = (userData) => {
    setUserInfo(userData);
    localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(userData));
  };

  const isAuthenticated = () => {
    return userInfo !== null;
  };

  // Get device ID (browser fingerprint)
  const getDeviceId = () => {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      // Generate a simple device ID based on browser info
      deviceId = `web-${navigator.userAgent}-${Date.now()}`.replace(/[^a-zA-Z0-9]/g, '').substring(0, 50);
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  };

  return (
    <AuthContext.Provider
      value={{
        userInfo,
        login,
        logout,
        updateUser,
        loading,
        isLoading,
        setIsLoading,
        message,
        setMessage,
        isAuthenticated,
        getDeviceId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;

